/**
 * RobotLabScene — Dev tool for testing robot damage, cracks, smoke, ejection,
 * stability and destruction by clicking on individual components.
 *
 * Features:
 * - Click components to damage them (cracks, smoke, break, ejection)
 * - Topple left/right to test stability and fall damage
 * - Energy drain to test paralysis and energy-save mode
 * - Spawn enemy to observe robot-enemy interaction
 * - Center of mass overlay (red dot)
 * - Camera follows robot, zoom via mouse wheel
 *
 * Accessible from the title screen "ROBOT LAB" button.
 */

import Phaser from 'phaser';
import { CANVAS_WIDTH, CANVAS_HEIGHT, COMPONENT_SPACING, TILE_SIZE, EnemyType, EnemyGrade, EnemyWeaponType } from '@/config/GameConfig';
import { ComponentType, ComponentCategory } from '@/components/ComponentDefs';
import { Robot, InstalledComponent } from '@/entities/Robot';
import { Enemy } from '@/entities/Enemy';
import { ROBOT_TEMPLATES } from '@/config/RobotTemplates';
import { createTechButton } from '@/ui/TechUI';
import { SoundManager } from '@/systems/SoundManager';
import { GOD } from '@/config/GodMode';
import { updateRobotPhysics } from '@/systems/RobotPhysics';
import { runBehaviorTick, BehaviorContext } from '@/behaviors/BehaviorEngine';
import { BehaviorRule } from '@/behaviors/BehaviorDefs';
import { ProjectileManager } from '@/systems/ProjectileManager';
import { EnemyProjectileManager } from '@/systems/EnemyProjectileManager';
import { EventBus, GameEvents } from '@/events/EventBus';

// ── Template type (extends RobotTemplate with evoLevel) ──
interface LabTemplate {
  name: string;
  components: { type: ComponentType; lx: number; ly: number; rotation?: number; flipX?: boolean; flipY?: boolean; evoLevel?: number }[];
  rules: BehaviorRule[];
}

// Available enemy types and grades for cycling
const ENEMY_TYPES = Object.values(EnemyType);
const ENEMY_GRADES = Object.values(EnemyGrade);

export class RobotLabScene extends Phaser.Scene {
  private robot: Robot | null = null;
  private robotRules: BehaviorRule[] = [];
  private infoText!: Phaser.GameObjects.Text;
  private compInfoText!: Phaser.GameObjects.Text;
  private stabText!: Phaser.GameObjects.Text;
  private templateIndex = 0;
  private templates: LabTemplate[] = [];
  private damageAmount = 15;
  private labScale = 2.5;
  private simTimer?: Phaser.Time.TimerEvent;
  private zoomText!: Phaser.GameObjects.Text;
  private uiObjects: Phaser.GameObjects.GameObject[] = [];
  private hudCam!: Phaser.Cameras.Scene2D.Camera;

  // Simulation state
  private simRunning = false;
  private enemies: Enemy[] = [];
  private enemyTypeIndex = 0;
  private enemyGradeIndex = 0;
  private comDot?: Phaser.GameObjects.Arc;      // center of mass overlay
  private energyDraining = false;
  private energyDrainTimer?: Phaser.Time.TimerEvent;

  // Projectile managers (robot + enemy)
  private projectileManager!: ProjectileManager;
  private enemyProjectileManager!: EnemyProjectileManager;

  // Range circles overlay
  private rangeGfx?: Phaser.GameObjects.Graphics;
  private showRangeCircles = true;

  // Ground contact line overlay
  private groundGfx?: Phaser.GameObjects.Graphics;
  // World-space status label (follows robot, shows topple/stability)
  private worldStatusText?: Phaser.GameObjects.Text;

  constructor() {
    super({ key: 'RobotLabScene' });
  }

  create(): void {
    // ── Background & grid (world-layer, will zoom) ──
    const bg = this.add.graphics();
    bg.fillStyle(0x0a0a1a, 1);
    bg.fillRect(-2000, -2000, 4000 + CANVAS_WIDTH, 4000 + CANVAS_HEIGHT);
    bg.lineStyle(1, 0x222244, 0.3);
    for (let x = -2000; x < CANVAS_WIDTH + 2000; x += 40) bg.lineBetween(x, -2000, x, CANVAS_HEIGHT + 2000);
    for (let y = -2000; y < CANVAS_HEIGHT + 2000; y += 40) bg.lineBetween(-2000, y, CANVAS_WIDTH + 2000, y);

    // Load templates from WorkshopScene (including rules)
    this.templates = ROBOT_TEMPLATES.map(t => ({
      name: t.name,
      components: t.components,
      rules: t.rules,
    }));

    // ── Setup cameras ──
    this.cameras.main.setZoom(this.labScale);
    this.cameras.main.centerOn(CANVAS_WIDTH / 2, CANVAS_HEIGHT / 2);

    // HUD camera: fixed, no zoom, renders UI only
    this.hudCam = this.cameras.add(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
    this.hudCam.setScroll(0, 0);

    // ── Projectile managers ──
    this.projectileManager = new ProjectileManager(this);
    this.enemyProjectileManager = new EnemyProjectileManager(this);
    EventBus.on(GameEvents.ENEMY_FIRED, this.onEnemyFired, this);

    // ── Range circles overlay (world-layer) ──
    this.rangeGfx = this.add.graphics().setDepth(5);

    // ── Ground contact line (world-layer) ──
    this.groundGfx = this.add.graphics().setDepth(4);

    // ── World-space status label (world-layer, follows robot) ──
    this.worldStatusText = this.add.text(0, 0, '', {
      fontSize: '14px', color: '#ffcc00', fontFamily: 'monospace', fontStyle: 'bold',
      stroke: '#000000', strokeThickness: 3, backgroundColor: '#00000088',
      padding: { x: 4, y: 2 },
    }).setOrigin(0.5, 1).setDepth(100).setScale(0.4);

    // ── Center of mass dot (world-layer) ──
    this.comDot = this.add.circle(0, 0, 3, 0xff2222, 0.9).setDepth(50);
    this.comDot.setStrokeStyle(1, 0xffffff, 0.6);
    this.comDot.setVisible(false);

    // ── UI elements (HUD-layer) — must be built BEFORE spawnRobot ──
    this.buildControlPanel();
    this.buildInfoPanel();

    // Title
    const title = this.add.text(CANVAS_WIDTH / 2, 20, 'ROBOT LAB — Damage Simulator', {
      fontSize: '18px', color: '#ffcc00', fontFamily: 'monospace', fontStyle: 'bold',
    }).setOrigin(0.5, 0);
    this.uiObjects.push(title);

    // Zoom display
    this.zoomText = this.add.text(CANVAS_WIDTH / 2, CANVAS_HEIGHT - 25, `Zoom: ${this.labScale.toFixed(1)}×`, {
      fontSize: '10px', color: '#666688', fontFamily: 'monospace',
    }).setOrigin(0.5, 0.5);
    this.uiObjects.push(this.zoomText);

    // Back button
    this.ui(createTechButton(this, 60, CANVAS_HEIGHT - 25, 'BACK', {
      onClick: () => { SoundManager.play('click'); this.cleanup(); this.scene.start('TitleScene'); },
      width: 80, height: 28, fontSize: '12px', color: 0x888888,
    }));

    // ESC → back to TitleScene
    this.input.keyboard?.on('keydown-ESC', () => {
      SoundManager.play('click');
      this.cleanup();
      this.scene.start('TitleScene');
    });

    // Mouse wheel zoom
    this.input.on('wheel', (_p: Phaser.Input.Pointer, _go: Phaser.GameObjects.GameObject[], _dx: number, dy: number) => {
      this.labScale = Phaser.Math.Clamp(this.labScale + (dy < 0 ? 0.3 : -0.3), 0.5, 8);
      this.cameras.main.setZoom(this.labScale);
      this.zoomText.setText(`Zoom: ${this.labScale.toFixed(1)}×`);
    });

    // Make main camera ignore all UI
    for (const obj of this.uiObjects) {
      this.cameras.main.ignore(obj);
    }

    // Periodically sync HUD cam ignore list — any non-UI child should be
    // hidden from HUD cam to prevent miniature ghost duplicates.
    this.time.addEvent({
      delay: 500, loop: true,
      callback: () => {
        if (!this.hudCam) return;
        for (const child of this.children.list) {
          if (!this.uiObjects.includes(child)) {
            this.hudCam.ignore(child);
          }
        }
      },
    });

    // ── Spawn first robot (world-layer, after UI is built) ──
    this.spawnRobot();

    // ── Simulation loop (always runs, handles physics + camera + CoM) ──
    this.simTimer = this.time.addEvent({
      delay: 16, loop: true,
      callback: () => this.simTick(16),
    });

    // Component click interaction (registered once)
    this.setupComponentInteraction();
  }

  // ═══════════════════════════════════════════
  //  SIMULATION TICK
  // ═══════════════════════════════════════════

  private simTick(dt: number): void {
    if (!this.robot) return;

    if (this.robot.alive) {
      // Run behavior when sim is active OR enemies are present (robot should fight back)
      const liveEnemies = this.enemies.filter(e => e.alive);
      if (this.simRunning || liveEnemies.length > 0) {
        const ctx: BehaviorContext = {
          robot: this.robot,
          enemies: liveEnemies,
          allies: [this.robot],
          delta: dt,
          baseX: this.robot.x,
          baseY: this.robot.y,
          scene: this,
          projectileManager: this.projectileManager,
        };
        runBehaviorTick(this.robotRules, ctx);
      }
      // Always run physics so force push / knockback work outside sim
      updateRobotPhysics(this.robot, dt);
      // Step topple physics (rigid-body rotation under gravity)
      this.robot.updateTopplePhysics(dt);

      // Update robot projectiles
      this.projectileManager.update(dt);

      // Update enemy projectiles (need robot as target)
      const robotTargets = this.robot.alive ? [{
        x: this.robot.x, y: this.robot.y, alive: this.robot.alive,
        takeDamage: (d: number, ax?: number, ay?: number) => this.robot!.takeDamage(d, ax, ay),
        collisionRadius: 20,
      }] : [];
      this.enemyProjectileManager.update(dt, robotTargets);

      // Update enemies — force aggro so they all chase the robot
      const liveRobots = this.robot.alive ? [{ x: this.robot.x, y: this.robot.y, alive: true }] : [];
      for (const e of this.enemies) {
        if (!e.alive) continue;
        // In lab, all enemies aggro toward robot (not just AGGRO_ENEMY_TYPES)
        e.canAggro = true;
        e.updateAggroTarget(liveRobots, undefined, dt / 1000);
        e.update(dt);
        e.updateShooting(dt / 1000, liveRobots);
      }

      // Update overlays
      this.robot.updateDebugOverlays();
      this.updateComDot();
      this.updateRangeCircles();
      this.updateGroundContact();
      this.updateWorldStatus();
    }

    // Camera follows robot
    if (this.robot) {
      this.cameras.main.centerOn(this.robot.x, this.robot.y);
    }

    // Refresh info periodically (every ~4 frames)
    if (Math.random() < 0.25) this.updateInfoText();
  }

  private updateComDot(): void {
    if (!this.comDot || !this.robot?.alive) {
      this.comDot?.setVisible(false);
      return;
    }
    const r = this.robot;
    const s = r.renderScale;
    const sm = GOD.componentSpacingMult;
    const flipX = r.flipped ? -1 : 1;
    const rot = r.container.rotation;
    const cosR = Math.cos(rot);
    const sinR = Math.sin(rot);

    // CoM in local coords
    const lx = flipX * r.stability.comX * sm * s;
    const ly = r.stability.comY * sm * s;
    // Convert to world — use container position (accounts for topple offset)
    const cx = r.container.x;
    const cy = r.container.y;
    const wx = cx + lx * cosR - ly * sinR;
    const wy = cy + lx * sinR + ly * cosR;

    this.comDot.setPosition(wx, wy);
    this.comDot.setVisible(true);
  }

  // ═══════════════════════════════════════════
  //  RANGE CIRCLES OVERLAY
  // ═══════════════════════════════════════════

  private updateRangeCircles(): void {
    if (!this.rangeGfx || !this.robot?.alive) {
      this.rangeGfx?.clear();
      return;
    }
    this.rangeGfx.clear();
    if (!this.showRangeCircles) return;

    const r = this.robot;

    // Sensor range — cyan dashed circle
    if (r.sensorRange > 0) {
      this.rangeGfx.lineStyle(1, 0x44cccc, 0.35);
      this.rangeGfx.strokeCircle(r.x, r.y, r.sensorRange);
    }

    // Weapon ranges — one circle per unique weapon range (orange)
    const weaponRanges = new Set<number>();
    for (const comp of r.components) {
      if (comp.broken || comp.def.category !== ComponentCategory.WEAPON) continue;
      if (comp.def.weaponRange > 0) weaponRanges.add(comp.def.weaponRange);
    }
    for (const range of weaponRanges) {
      this.rangeGfx.lineStyle(1, 0xff8844, 0.35);
      this.rangeGfx.strokeCircle(r.x, r.y, range);
    }
  }

  // ═══════════════════════════════════════════
  //  GROUND CONTACT LINE
  // ═══════════════════════════════════════════

  private updateGroundContact(): void {
    if (!this.groundGfx || !this.robot?.alive) {
      this.groundGfx?.clear();
      return;
    }
    this.groundGfx.clear();

    const r = this.robot;
    const sm = GOD.componentSpacingMult;
    const s = r.renderScale;
    const flipX = r.flipped ? -1 : 1;
    const rot = r.container.rotation;
    const cosR = Math.cos(rot);
    const sinR = Math.sin(rot);
    const cx = r.container.x;
    const cy = r.container.y;

    const active = r.components.filter(c => !c.broken);
    if (active.length === 0) return;

    // Find working wheels (real supports)
    const wheels = active.filter(c => c.def.category === ComponentCategory.MOVEMENT);

    // Fallback: bottommost row when no wheels
    const maxY = Math.max(...active.map(c => c.localY));
    const bottomRow = active.filter(c => Math.abs(c.localY - maxY) < COMPONENT_SPACING * 0.5);
    const supportComps = wheels.length > 0 ? wheels : bottomRow;

    // Convert ALL component positions to world coords to find visual bounding box
    const allWorldPts: { x: number; y: number }[] = [];
    for (const comp of active) {
      const lx = flipX * comp.localX * sm * s;
      const ly = comp.localY * sm * s;
      allWorldPts.push({
        x: cx + lx * cosR - ly * sinR,
        y: cy + lx * sinR + ly * cosR,
      });
    }

    // Convert support positions to world coords
    const supportPts: { x: number; y: number }[] = [];
    for (const comp of supportComps) {
      const lx = flipX * comp.localX * sm * s;
      const ly = comp.localY * sm * s;
      supportPts.push({
        x: cx + lx * cosR - ly * sinR,
        y: cy + lx * sinR + ly * cosR,
      });
    }
    if (supportPts.length === 0) return;

    // Ground Y = bottom of the lowest support + half a grid cell
    const halfCell = COMPONENT_SPACING * s * 0.5;
    const groundY = Math.max(...supportPts.map(p => p.y)) + halfCell;

    // Ground line: extends beyond the robot's full width
    const allX = allWorldPts.map(p => p.x);
    const minX = Math.min(...allX) - halfCell;
    const maxX = Math.max(...allX) + halfCell;

    // Draw ground line — green=wheels, orange=fallback
    const color = wheels.length > 0 ? 0x44ff44 : 0xff8844;

    // Filled ground strip (semi-transparent background)
    this.groundGfx.fillStyle(color, 0.15);
    this.groundGfx.fillRect(minX, groundY - 1, maxX - minX, 3);

    // Thick ground line
    this.groundGfx.lineStyle(3, color, 0.9);
    this.groundGfx.lineBetween(minX, groundY, maxX, groundY);

    // Draw support contact dots (projected onto ground line)
    for (const p of supportPts) {
      this.groundGfx.fillStyle(color, 1);
      this.groundGfx.fillCircle(p.x, groundY, 4);
      // Vertical line from support to ground
      this.groundGfx.lineStyle(2, color, 0.5);
      this.groundGfx.lineBetween(p.x, p.y, p.x, groundY);
    }

    // Draw CoM projected down to ground (red)
    const comLx = flipX * r.stability.comX * sm * s;
    const comLy = r.stability.comY * sm * s;
    const comWx = cx + comLx * cosR - comLy * sinR;
    this.groundGfx.lineStyle(2, 0xff2222, 0.7);
    this.groundGfx.lineBetween(comWx, cy - halfCell * 3, comWx, groundY);
    // Red triangle at ground
    this.groundGfx.fillStyle(0xff2222, 1);
    this.groundGfx.fillTriangle(comWx - 4, groundY, comWx + 4, groundY, comWx, groundY - 6);
  }

  // ═══════════════════════════════════════════
  //  WORLD-SPACE STATUS LABEL
  // ═══════════════════════════════════════════

  private updateWorldStatus(): void {
    if (!this.worldStatusText || !this.robot?.alive) {
      this.worldStatusText?.setVisible(false);
      return;
    }
    const r = this.robot;
    const s = r.renderScale;
    const sm = GOD.componentSpacingMult;
    // Position above robot (using topmost component)
    const active = r.components.filter(c => !c.broken);
    const minLocalY = active.length > 0 ? Math.min(...active.map(c => c.localY)) : 0;
    const aboveY = r.container.y + minLocalY * sm * s - COMPONENT_SPACING * s * 2.5;

    const wheels = r.components.filter(c => c.def.category === ComponentCategory.MOVEMENT);
    const workingWheels = wheels.filter(c => !c.broken);
    const tiltDeg = (r.container.rotation * 180 / Math.PI).toFixed(0);
    const lines = [
      `stab=${r.stability.score.toFixed(2)} [${r.stability.label}]`,
      `wheels=${workingWheels.length}/${wheels.length} tilt=${tiltDeg}°`,
      r.isFallen ? `FALLEN (spd=${r.speed.toFixed(0)}px/s)` : '',
      r.canFly() ? `spin=${r.spinRate.toFixed(1)} rad/s` : '',
    ].filter(Boolean);
    this.worldStatusText.setText(lines.join('\n'));
    // Counter-scale so text stays readable regardless of camera zoom
    const invZoom = 1 / this.cameras.main.zoom;
    this.worldStatusText.setScale(invZoom);
    this.worldStatusText.setPosition(r.container.x, aboveY);
    this.worldStatusText.setVisible(true);
  }

  // ═══════════════════════════════════════════
  //  ENEMY FIRED HANDLER
  // ═══════════════════════════════════════════

  private onEnemyFired = (data: {
    sx: number; sy: number; tx: number; ty: number;
    damage: number; speed: number; color: number; range: number;
    weaponType: EnemyWeaponType;
    enemyType: string;
    coneAngle?: number; flameDuration?: number;
  }): void => {
    if (!this.robot?.alive) return;
    const robotTargets = [{
      x: this.robot.x, y: this.robot.y, alive: this.robot.alive,
      takeDamage: (d: number, ax?: number, ay?: number) => this.robot!.takeDamage(d, ax, ay),
      collisionRadius: 20,
    }];

    switch (data.weaponType) {
      case EnemyWeaponType.LASER:
        this.enemyProjectileManager.spawnBeam(
          data.sx, data.sy, data.tx, data.ty, data.damage, data.color, robotTargets,
        );
        break;
      case EnemyWeaponType.FLAMETHROWER:
        this.enemyProjectileManager.spawnFlame(
          data.sx, data.sy, data.tx, data.ty,
          data.damage, data.range, data.color, data.coneAngle ?? 0.5, data.flameDuration ?? 800,
        );
        break;
      default:
        this.enemyProjectileManager.spawnProjectile(
          data.sx, data.sy, data.tx, data.ty,
          data.damage, data.speed, data.color, data.range,
          data.weaponType,
        );
        break;
    }

    // Play enemy shoot SFX
    const sfxMap: Record<string, import('@/systems/SoundManager').SfxKey> = {
      tank: 'enemyShootTank', drone: 'enemyShootDrone',
      bomber: 'enemyShootBomber', brute: 'enemyShootBrute',
      specter: 'enemyShootSpecter',
    };
    SoundManager.play(sfxMap[data.enemyType] ?? 'shoot');
  };

  // ═══════════════════════════════════════════
  //  FORCE PUSH
  // ═══════════════════════════════════════════

  private applyForcePush(dirX: number, dirY: number): void {
    if (!this.robot?.alive) return;
    const PUSH_FORCE = 250; // px/s impulse — strong enough to be visible
    this.robot.vx += dirX * PUSH_FORCE;
    this.robot.vy += dirY * PUSH_FORCE;
    SoundManager.play('hit');
  }

  // ═══════════════════════════════════════════
  //  UI HELPER
  // ═══════════════════════════════════════════

  private ui<T extends Phaser.GameObjects.GameObject>(obj: T): T {
    this.uiObjects.push(obj);
    return obj;
  }

  // ═══════════════════════════════════════════
  //  CONTROL PANEL (left side)
  // ═══════════════════════════════════════════

  private buildControlPanel(): void {
    const px = 100;
    let y = 55;

    // ── Template selector ──
    this.ui(this.add.text(px, y, 'TEMPLATE', {
      fontSize: '11px', color: '#aaaacc', fontFamily: 'monospace',
    }).setOrigin(0.5, 0));
    y += 20;
    this.ui(createTechButton(this, px - 50, y, '◀', {
      onClick: () => { this.prevTemplate(); },
      width: 30, height: 24, fontSize: '14px', color: 0x6688aa,
    }));
    this.ui(createTechButton(this, px + 50, y, '▶', {
      onClick: () => { this.nextTemplate(); },
      width: 30, height: 24, fontSize: '14px', color: 0x6688aa,
    }));
    y += 30;

    // ── Damage amount ──
    this.ui(this.add.text(px, y, 'DAMAGE PER CLICK', {
      fontSize: '10px', color: '#aaaacc', fontFamily: 'monospace',
    }).setOrigin(0.5, 0));
    y += 16;
    const dmgValues = [5, 10, 15, 25, 50, 100, 9999];
    const dmgLabels = ['5', '10', '15', '25', '50', '100', 'KILL'];
    for (let i = 0; i < dmgValues.length; i++) {
      const val = dmgValues[i];
      this.ui(createTechButton(this, px - 60 + (i % 4) * 40, y + Math.floor(i / 4) * 26, dmgLabels[i], {
        onClick: () => { this.damageAmount = val; SoundManager.play('click'); },
        width: 36, height: 22, fontSize: '9px',
        color: val === 9999 ? 0xff4444 : 0x6688aa,
      }));
    }
    y += 60;

    // ── Action buttons ──
    this.ui(createTechButton(this, px, y, 'REPAIR ALL', {
      onClick: () => { this.repairAll(); },
      width: 100, height: 24, fontSize: '10px', color: 0x44cc44,
    }));
    y += 28;
    this.ui(createTechButton(this, px, y, 'RESET ROBOT', {
      onClick: () => { this.spawnRobot(); },
      width: 100, height: 24, fontSize: '10px', color: 0x4488cc,
    }));
    y += 32;

    // ── Topple ──
    this.ui(this.add.text(px, y, 'TOPPLE', {
      fontSize: '10px', color: '#aaaacc', fontFamily: 'monospace',
    }).setOrigin(0.5, 0));
    y += 16;
    this.ui(createTechButton(this, px - 35, y, '◀ LEFT', {
      onClick: () => { if (this.robot?.alive && !this.robot.isFallen) this.robot.topple(-1); },
      width: 60, height: 24, fontSize: '9px', color: 0xcc8844,
    }));
    this.ui(createTechButton(this, px + 35, y, 'RIGHT ▶', {
      onClick: () => { if (this.robot?.alive && !this.robot.isFallen) this.robot.topple(1); },
      width: 60, height: 24, fontSize: '9px', color: 0xcc8844,
    }));
    y += 28;

    // ── Break All Wheels (instant topple test) ──
    this.ui(createTechButton(this, px, y, 'BREAK WHEELS', {
      onClick: () => { this.breakAllWheels(); },
      width: 100, height: 24, fontSize: '10px', color: 0xff4444,
    }));
    y += 32;

    // ── Energy drain ──
    this.ui(createTechButton(this, px, y, 'DRAIN ENERGY', {
      onClick: () => { this.toggleEnergyDrain(); },
      width: 100, height: 24, fontSize: '10px', color: 0xcccc44,
    }));
    y += 28;
    this.ui(createTechButton(this, px, y, 'REFILL ENERGY', {
      onClick: () => {
        if (this.robot?.alive) {
          this.robot.currentEnergy = this.robot.maxEnergy;
          this.robot.energySaveMode = false;
          SoundManager.play('clickConfirm');
        }
      },
      width: 100, height: 24, fontSize: '10px', color: 0x44cccc,
    }));
    y += 36;

    // ── Enemy spawn ──
    this.ui(this.add.text(px, y, 'ENEMY', {
      fontSize: '10px', color: '#aaaacc', fontFamily: 'monospace',
    }).setOrigin(0.5, 0));
    y += 16;
    this.ui(createTechButton(this, px, y, 'SPAWN ENEMY', {
      onClick: () => { this.spawnEnemy(); },
      width: 100, height: 24, fontSize: '10px', color: 0xff6644,
    }));
    y += 26;
    this.ui(createTechButton(this, px - 35, y, '◀ TYPE', {
      onClick: () => {
        this.enemyTypeIndex = (this.enemyTypeIndex - 1 + ENEMY_TYPES.length) % ENEMY_TYPES.length;
        SoundManager.play('click');
        this.updateInfoText();
      },
      width: 60, height: 22, fontSize: '9px', color: 0x886644,
    }));
    this.ui(createTechButton(this, px + 35, y, 'TYPE ▶', {
      onClick: () => {
        this.enemyTypeIndex = (this.enemyTypeIndex + 1) % ENEMY_TYPES.length;
        SoundManager.play('click');
        this.updateInfoText();
      },
      width: 60, height: 22, fontSize: '9px', color: 0x886644,
    }));
    y += 24;
    // Grade selector
    this.ui(createTechButton(this, px - 35, y, '◀ GRADE', {
      onClick: () => {
        this.enemyGradeIndex = (this.enemyGradeIndex - 1 + ENEMY_GRADES.length) % ENEMY_GRADES.length;
        SoundManager.play('click');
        this.updateInfoText();
      },
      width: 60, height: 22, fontSize: '9px', color: 0x886644,
    }));
    this.ui(createTechButton(this, px + 35, y, 'GRADE ▶', {
      onClick: () => {
        this.enemyGradeIndex = (this.enemyGradeIndex + 1) % ENEMY_GRADES.length;
        SoundManager.play('click');
        this.updateInfoText();
      },
      width: 60, height: 22, fontSize: '9px', color: 0x886644,
    }));
    y += 26;
    this.ui(createTechButton(this, px, y, 'CLEAR ENEMIES', {
      onClick: () => { this.clearEnemies(); },
      width: 100, height: 22, fontSize: '9px', color: 0x884444,
    }));
    y += 32;

    // ── Force push ──
    this.ui(this.add.text(px, y, 'FORCE PUSH', {
      fontSize: '10px', color: '#aaaacc', fontFamily: 'monospace',
    }).setOrigin(0.5, 0));
    y += 16;
    this.ui(createTechButton(this, px - 35, y, '◀ LEFT', {
      onClick: () => { this.applyForcePush(-1, 0); },
      width: 60, height: 22, fontSize: '9px', color: 0x8866cc,
    }));
    this.ui(createTechButton(this, px + 35, y, 'RIGHT ▶', {
      onClick: () => { this.applyForcePush(1, 0); },
      width: 60, height: 22, fontSize: '9px', color: 0x8866cc,
    }));
    y += 24;
    this.ui(createTechButton(this, px - 35, y, '▲ UP', {
      onClick: () => { this.applyForcePush(0, -1); },
      width: 60, height: 22, fontSize: '9px', color: 0x8866cc,
    }));
    this.ui(createTechButton(this, px + 35, y, '▼ DOWN', {
      onClick: () => { this.applyForcePush(0, 1); },
      width: 60, height: 22, fontSize: '9px', color: 0x8866cc,
    }));
    y += 30;

    // ── Simulation toggle ──
    this.ui(createTechButton(this, px, y, 'TOGGLE SIM', {
      onClick: () => {
        this.simRunning = !this.simRunning;
        SoundManager.play('click');
      },
      width: 100, height: 24, fontSize: '10px', color: 0x88cc88,
    }));
    y += 24;

    // ── Range circles toggle ──
    this.ui(createTechButton(this, px, y, 'TOGGLE RANGES', {
      onClick: () => {
        this.showRangeCircles = !this.showRangeCircles;
        SoundManager.play('click');
        if (!this.showRangeCircles) this.rangeGfx?.clear();
      },
      width: 100, height: 22, fontSize: '9px', color: 0x44aaaa,
    }));
    y += 18;
    this.ui(this.add.text(px, y, 'Weapon + sensor range', {
      fontSize: '8px', color: '#555577', fontFamily: 'monospace', align: 'center',
    }).setOrigin(0.5, 0));
  }

  // ═══════════════════════════════════════════
  //  INFO PANEL (right side)
  // ═══════════════════════════════════════════

  private buildInfoPanel(): void {
    const rx = CANVAS_WIDTH - 180;

    this.infoText = this.ui(this.add.text(rx, 50, '', {
      fontSize: '13px', color: '#ffcc00', fontFamily: 'monospace', fontStyle: 'bold',
      wordWrap: { width: 300 },
    }).setOrigin(0.5, 0));

    this.stabText = this.ui(this.add.text(rx, 80, '', {
      fontSize: '10px', color: '#88aacc', fontFamily: 'monospace',
      wordWrap: { width: 300 },
      lineSpacing: 3,
    }).setOrigin(0.5, 0));

    this.compInfoText = this.ui(this.add.text(rx, CANVAS_HEIGHT - 180, '', {
      fontSize: '10px', color: '#aaaaaa', fontFamily: 'monospace',
      wordWrap: { width: 300 },
      lineSpacing: 3,
    }).setOrigin(0.5, 0));
  }

  // ═══════════════════════════════════════════
  //  ROBOT SPAWNING
  // ═══════════════════════════════════════════

  private spawnRobot(): void {
    if (this.robot) {
      if (this.robot.alive) this.robot.destroy();
      this.robot = null;
    }
    this.clearEnemies();
    this.stopEnergyDrain();

    const tpl = this.templates[this.templateIndex];
    if (!tpl) return;

    const robot = new Robot(this, CANVAS_WIDTH / 2, CANVAS_HEIGHT / 2);
    for (const c of tpl.components) {
      robot.addComponent(c.type, c.lx, c.ly, c.rotation ?? 0, c.flipX ?? false, c.flipY ?? false, c.evoLevel ?? 0);
    }

    // Set heading BEFORE resetHpEnergy so stability uses correct flight axis
    const dirComp = tpl.components.find(c => c.type === ComponentType.DIRECTION);
    if (dirComp) {
      const rot = dirComp.rotation ?? 0;
      robot.heading = rot;
      robot.buildHeading = rot;
      robot.recalcStats();
    }

    robot.resetHpEnergy();
    robot.normalizeSize();

    this.cameras.main.setZoom(this.labScale);
    robot.placed = true;

    robot.updateDebugOverlays();

    this.robot = robot;
    this.robotRules = [...tpl.rules];

    // Hide all world objects from HUD cam
    if (this.hudCam) {
      this.hudCam.ignore(robot.container);
      for (const child of this.children.list) {
        if (!this.uiObjects.includes(child)) {
          this.hudCam.ignore(child);
        }
      }
    }

    this.updateInfoText();
  }

  // ═══════════════════════════════════════════
  //  ENEMY SPAWNING
  // ═══════════════════════════════════════════

  private spawnEnemy(): void {
    if (!this.robot) return;
    SoundManager.play('click');

    const type = ENEMY_TYPES[this.enemyTypeIndex];
    // Spawn enemy at random angle around robot
    const spawnDist = 120 + Math.random() * 60;
    const angle = Math.random() * Math.PI * 2;
    const ex = this.robot.x + Math.cos(angle) * spawnDist;
    const ey = this.robot.y + Math.sin(angle) * spawnDist;

    // Waypoints lead toward robot
    const waypoints = [
      { x: ex, y: ey },
      { x: this.robot.x, y: this.robot.y },
    ];
    const grade = ENEMY_GRADES[this.enemyGradeIndex];
    const enemy = new Enemy(this, waypoints, type, 5, grade);
    this.enemies.push(enemy);

    // Hide from HUD cam
    if (this.hudCam) {
      this.hudCam.ignore(enemy.body);
      this.hudCam.ignore(enemy.hpBar);
      this.hudCam.ignore(enemy.hpBarBg);
      this.hudCam.ignore(enemy.label);
    }
  }

  private clearEnemies(): void {
    for (const e of this.enemies) {
      if (e.alive) e.destroy();
    }
    this.enemies = [];
  }

  // ═══════════════════════════════════════════
  //  ENERGY DRAIN
  // ═══════════════════════════════════════════

  private toggleEnergyDrain(): void {
    if (this.energyDraining) {
      this.stopEnergyDrain();
    } else {
      this.energyDraining = true;
      SoundManager.play('click');
      this.energyDrainTimer = this.time.addEvent({
        delay: 100, loop: true,
        callback: () => {
          if (!this.robot?.alive) return;
          this.robot.currentEnergy = Math.max(0, this.robot.currentEnergy - this.robot.maxEnergy * 0.03);
        },
      });
    }
  }

  private stopEnergyDrain(): void {
    this.energyDraining = false;
    if (this.energyDrainTimer) {
      this.energyDrainTimer.destroy();
      this.energyDrainTimer = undefined;
    }
  }

  // ═══════════════════════════════════════════
  //  COMPONENT INTERACTION (click to damage, hover to inspect)
  // ═══════════════════════════════════════════

  private setupComponentInteraction(): void {
    this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      if (!this.robot || !this.robot.alive) return;

      const worldPoint = this.cameras.main.getWorldPoint(pointer.x, pointer.y);
      const comp = this.findComponentAtWorld(worldPoint.x, worldPoint.y);

      if (comp) {
        SoundManager.play('hit');
        this.applyDirectDamage(comp, this.damageAmount);
      }
    });

    this.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      if (!this.robot || !this.robot.alive) return;
      const worldPoint = this.cameras.main.getWorldPoint(pointer.x, pointer.y);
      const comp = this.findComponentAtWorld(worldPoint.x, worldPoint.y);
      if (comp) {
        this.updateCompInfo(comp);
      } else {
        this.compInfoText.setText('');
      }
    });
  }

  private applyDirectDamage(comp: InstalledComponent, amount: number): void {
    if (!this.robot || comp.broken) return;

    comp.compCurrentHp -= amount;
    if (comp.compCurrentHp <= 0) {
      comp.compCurrentHp = 0;
      comp.broken = true;
      (this.robot as any).onComponentBroken(comp);
    } else {
      (this.robot as any).updateCracks(comp);
    }
    (this.robot as any).updateCompSmoke(comp);
    this.robot.updateDebugOverlays();
    this.updateInfoText();
  }

  /** Find component at world coordinates */
  private findComponentAtWorld(wx: number, wy: number): InstalledComponent | null {
    if (!this.robot || !this.robot.alive) return null;

    const containerScale = Math.abs(this.robot.container.scaleX);
    const containerRot = this.robot.container.rotation;
    const cosR = Math.cos(-containerRot);
    const sinR = Math.sin(-containerRot);
    const dx = wx - this.robot.x;
    const dy = wy - this.robot.y;
    const rdx = dx * cosR - dy * sinR;
    const rdy = dx * sinR + dy * cosR;
    const localX = rdx / containerScale;
    const localY = rdy / containerScale;

    const sm = GOD.componentSpacingMult;
    const hitRadius = COMPONENT_SPACING * 0.6;
    let bestComp: InstalledComponent | null = null;
    let bestDist = Infinity;

    for (const comp of this.robot.components) {
      if (comp.broken && comp.ejected) continue;
      const flipX = this.robot.flipped ? -1 : 1;
      const cx = flipX * comp.localX * sm;
      const cy = comp.localY * sm;
      const d = Math.sqrt((localX - cx) ** 2 + (localY - cy) ** 2);
      if (d < hitRadius && d < bestDist) {
        bestDist = d;
        bestComp = comp;
      }
    }
    return bestComp;
  }

  // ═══════════════════════════════════════════
  //  INFO DISPLAY
  // ═══════════════════════════════════════════

  private updateCompInfo(comp: InstalledComponent): void {
    const hpPct = comp.compMaxHp > 0 ? Math.round(comp.compCurrentHp / comp.compMaxHp * 100) : 100;
    const status = comp.ejected ? 'EJECTED' : comp.broken ? 'BROKEN' : `${hpPct}% HP`;
    const lines = [
      `── ${comp.def.name} ──`,
      `Type: ${comp.def.type}`,
      `Category: ${comp.def.category}`,
      `HP: ${Math.round(comp.compCurrentHp)}/${Math.round(comp.compMaxHp)}`,
      `Status: ${status}`,
      `Crack level: ${comp.crackLevel}`,
      `Mass: ${comp.def.mass}`,
      `Exposed: ${comp.exposed ? 'yes' : 'no'}`,
    ];
    if (comp.def.category === ComponentCategory.WEAPON) {
      lines.push(`Damage: ${comp.def.damage}`);
      lines.push(`Range: ${(comp.def.weaponRange / (TILE_SIZE * Math.SQRT2)).toFixed(1)}D`);
    }
    if (comp.def.force > 0) lines.push(`Force: ${comp.def.force}`);
    if (comp.def.sensorRange > 0) lines.push(`Sensor: ${(comp.def.sensorRange / (TILE_SIZE * Math.SQRT2)).toFixed(1)}D`);
    this.compInfoText.setText(lines.join('\n'));
  }

  private updateInfoText(): void {
    const tpl = this.templates[this.templateIndex];
    this.infoText.setText(tpl ? `[${this.templateIndex + 1}/${this.templates.length}] ${tpl.name}` : 'No template');

    if (!this.robot) { this.stabText.setText(''); return; }

    const r = this.robot;
    const alive = r.components.filter(c => !c.broken);
    const broken = r.components.filter(c => c.broken && !c.ejected);
    const ejected = r.components.filter(c => c.ejected);
    const wheels = r.components.filter(c => c.def.type === ComponentType.WHEEL);
    const workingWheels = wheels.filter(c => !c.broken);
    const energyPct = r.maxEnergy > 0 ? Math.round(r.currentEnergy / r.maxEnergy * 100) : 0;

    const lines = [
      `─── ROBOT STATUS ───`,
      `HP: ${Math.round(r.currentHp)}/${r.totalHp}`,
      `Energy: ${Math.round(r.currentEnergy)}/${r.maxEnergy} (${energyPct}%)`,
      `State: ${r.state}`,
      `Fallen: ${r.isFallen}`,
      `Energy save: ${r.energySaveMode}`,
      `Sim: ${this.simRunning ? 'ON' : 'OFF'}`,
      ``,
      `─── COMPONENTS ───`,
      `Total: ${r.components.length}`,
      `Active: ${alive.length}`,
      `Broken: ${broken.length}`,
      `Ejected: ${ejected.length}`,
      ``,
      `─── STABILITY ───`,
      `Score: ${r.stability.score.toFixed(3)}`,
      `Tilt: ${(r.stability.tiltAngle * 180 / Math.PI).toFixed(1)}°`,
      `CoM: (${r.stability.comX.toFixed(1)}, ${r.stability.comY.toFixed(1)})`,
      `Supports: ${r.stability.supportCount}`,
      `Wheels: ${workingWheels.length}/${wheels.length}`,
      ``,
      `─── MOBILITY ───`,
      `Speed: ${r.speed.toFixed(1)} px/s`,
      `Mass: ${r.totalMass.toFixed(1)}`,
      r.canFly() ? `Flight: ${(r.flightIntegrity * 100).toFixed(0)}%` : '',
      ``,
      `─── LAB ───`,
      `Dmg/click: ${this.damageAmount}`,
      `Enemies: ${this.enemies.filter(e => e.alive).length}`,
      `Next enemy: ${ENEMY_TYPES[this.enemyTypeIndex]} [${ENEMY_GRADES[this.enemyGradeIndex]}]`,
      this.energyDraining ? `Energy: DRAINING` : '',
    ];

    this.stabText.setText(lines.filter(l => l !== '').join('\n'));
  }

  // ═══════════════════════════════════════════
  //  TEMPLATE NAVIGATION
  // ═══════════════════════════════════════════

  private nextTemplate(): void {
    this.templateIndex = (this.templateIndex + 1) % this.templates.length;
    SoundManager.play('click');
    this.spawnRobot();
  }

  private prevTemplate(): void {
    this.templateIndex = (this.templateIndex - 1 + this.templates.length) % this.templates.length;
    SoundManager.play('click');
    this.spawnRobot();
  }

  private repairAll(): void {
    if (!this.robot || !this.robot.alive) return;
    SoundManager.play('clickConfirm');
    // Stand up first if fallen
    if (this.robot.isFallen) this.robot.standUp();
    for (const comp of this.robot.getBrokenComponents()) {
      this.robot.repairComponent(comp);
    }
    this.robot.currentHp = this.robot.totalHp;
    this.robot.currentEnergy = this.robot.maxEnergy;
    this.robot.energySaveMode = false;
    this.robot.updateDebugOverlays();
    this.updateInfoText();
  }

  /** Instantly break all MOVEMENT components to test topple */
  private breakAllWheels(): void {
    if (!this.robot || !this.robot.alive) return;
    SoundManager.play('hit');
    const wheels = this.robot.components.filter(
      c => !c.broken && c.def.category === ComponentCategory.MOVEMENT,
    );
    if (wheels.length === 0) return;
    for (const comp of wheels) {
      comp.compCurrentHp = 0;
      comp.broken = true;
      (this.robot as any).onComponentBroken(comp);
    }
    this.robot.updateDebugOverlays();
    this.updateInfoText();
  }

  // ═══════════════════════════════════════════
  //  CLEANUP
  // ═══════════════════════════════════════════

  private cleanup(): void {
    this.input.removeAllListeners();
    this.stopEnergyDrain();
    EventBus.off(GameEvents.ENEMY_FIRED, this.onEnemyFired, this);
    if (this.simTimer) { this.simTimer.destroy(); this.simTimer = undefined; }
    this.clearEnemies();
    this.projectileManager.clear();
    this.enemyProjectileManager.clear();
    if (this.robot?.alive) this.robot.destroy();
    this.robot = null;
    this.comDot?.destroy();
    this.comDot = undefined;
    this.rangeGfx?.destroy();
    this.rangeGfx = undefined;
    this.groundGfx?.destroy();
    this.groundGfx = undefined;
    this.worldStatusText?.destroy();
    this.worldStatusText = undefined;
  }
}
